import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from 'react-use-cart';
import { useNotification } from '../../Contexts/NotificationContext';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import particlesConfig from '../../Shared/pr-s-black';
import '../../Assets/Stylesheets/Pages/MarketingServices/ProductDetail.scss';

// Import product data - can be replaced with API calls later
import pressReleaseData from '../../SampleData/pressReleaseProducts.json';
import videoProductsData from '../../SampleData/videoProducts.json';

// Map category to data source
const categoryDataMap = {
  'press-release': pressReleaseData,
  'videos': videoProductsData,
  // Future categories can be added here:
  // 'websites': websiteProductsData,
};

export default function ProductDetail() {
  const { category, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { showSuccess } = useNotification();

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  useEffect(() => {
    // Simulate API call - replace with actual Supabase query later
    const fetchProduct = () => {
      try {
        const categoryData = categoryDataMap[category];
        
        if (!categoryData) {
          console.error('Category not found');
          setLoading(false);
          return;
        }

        const foundProduct = categoryData.products.find(
          (p) => p.id === productId
        );

        if (foundProduct) {
          setProduct(foundProduct);
        } else {
          console.error('Product not found');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product:', error);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [category, productId]);

  const handleAddToCart = () => {
    if (product.customPricing || product.contactRequired || product.price <= 0) {
      // For custom pricing, navigate to contact page
      navigate('/contact');
      return;
    }

    const item = {
      id: `${category}-${productId}-${Date.now()}`,
      name: product.name,
      platform: getCategoryDisplayName(),
      price: product.price,
      description: product.description,
      type: category
    };
    
    addItem(item);
    showSuccess(`${product.name} added to cart!`);
  };

  const handleGoBack = () => {
    // Navigate back to the category page
    const categoryRoutes = {
      'press-release': '/pr',
      'videos': '/videos',
      // Add more category routes as needed
    };
    navigate(categoryRoutes[category] || '/marketing-services');
  };

  const getCategoryDisplayName = () => {
    const categoryNames = {
      'press-release': 'Press Releases',
      'videos': 'Videos',
    };
    return categoryNames[category] || 'Services';
  };

  if (loading) {
    return (
      <div className="product-detail-page loading">
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          className="particles-background"
        />
        <div className="loading-content">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page not-found">
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          className="particles-background"
        />
        <div className="not-found-content">
          <h2>Product Not Found</h2>
          <button onClick={handleGoBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Dynamic logo import
  const logoSrc = require(`../../Assets/Images/${product.logo}`);

  return (
    <div className="product-detail-page">
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={particlesConfig}
        className="particles-background"
      />
      
      <div className="product-detail-content">
        <button onClick={handleGoBack} className="back-button">
          ← Back to {getCategoryDisplayName()}
        </button>

        <div className="product-header">
          <div className="product-logo-large">
            <img src={logoSrc} alt={`${product.name} Logo`} />
          </div>
          <div className="product-title-section">
            <h1>{product.name}</h1>
            <p className="product-tagline">{product.description}</p>
            {product.price > 0 ? (
              <div className="product-price-large">
                <span className="currency">$</span>
                <span className="amount">{product.price}</span>
              </div>
            ) : (
              <div className="product-price-large custom">
                <span className="amount">Custom Pricing</span>
              </div>
            )}
          </div>
        </div>

        <div className="product-main-content">
          <div className="product-description-section">
            <h2>About This Service</h2>
            <p className="detailed-description">{product.detailedDescription}</p>
            
            <div className="product-stats">
              <div className="stat-item">
                <span className="stat-label">Delivery Time</span>
                <span className="stat-value">{product.deliveryTime}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Revisions</span>
                <span className="stat-value">{product.revisions}</span>
              </div>
              {category === 'press-release' && product.reach && (
                <div className="stat-item">
                  <span className="stat-label">Reach</span>
                  <span className="stat-value">{product.reach}</span>
                </div>
              )}
              {category === 'videos' && product.videoDuration && (
                <div className="stat-item">
                  <span className="stat-label">Video Duration</span>
                  <span className="stat-value">{product.videoDuration}</span>
                </div>
              )}
              {category === 'videos' && product.videoQuality && (
                <div className="stat-item">
                  <span className="stat-label">Quality</span>
                  <span className="stat-value">{product.videoQuality}</span>
                </div>
              )}
            </div>

            <div className="features-section">
              <h3>What's Included</h3>
              <ul className="features-list">
                {product.features.map((feature, index) => (
                  <li key={index}>
                    <span className="checkmark">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {product.benefits && (
              <div className="benefits-section">
                <h3>Key Benefits</h3>
                <ul className="benefits-list">
                  {product.benefits.map((benefit, index) => (
                    <li key={index}>
                      <span className="star">★</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.addOns && product.addOns.length > 0 && (
              <div className="addons-section">
                <h3>Available Add-Ons</h3>
                <ul className="addons-list">
                  {product.addOns.map((addon, index) => (
                    <li key={index}>
                      <span className="addon-icon">+</span>
                      {addon}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.examples && product.examples.length > 0 && (
              <div className="examples-section">
                <h3>Project Examples</h3>
                <ul className="examples-list">
                  {product.examples.map((example, index) => (
                    <li key={index}>
                      <span className="example-icon">▸</span>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="product-sidebar">
            <div className="purchase-card">
              <h3>Ready to Get Started?</h3>
              <div className="price-display">
                <span className="price-label">
                  {product.customPricing ? 'Custom Pricing' : 'Total Price'}
                </span>
                {product.price > 0 ? (
                  <div className="price-amount">
                    <span className="currency">$</span>
                    <span className="amount">{product.price}</span>
                  </div>
                ) : (
                  <div className="price-amount custom">
                    <span className="amount">Contact Us</span>
                  </div>
                )}
              </div>
              
              {product.targetAudience && (
                <div className="target-audience">
                  <h4>Perfect For:</h4>
                  <p>{product.targetAudience}</p>
                </div>
              )}

              <button onClick={handleAddToCart} className="add-to-cart-button">
                {product.customPricing || product.contactRequired ? 'Request Quote' : 'Add to Cart'}
              </button>
              
              <div className="guarantee">
                <p>✓ Satisfaction guaranteed</p>
                <p>✓ Professional support included</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
